
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRef } from 'react';
import { FiExternalLink } from 'react-icons/fi';
import styled from 'styled-components';

import nextI18NextConfig from '../../next-i18next.config.js';
import IntroSection from '@/components/wrappers/IntroSection';
import PageWrapper from '@/components/wrappers/PageWrapper';
import Section from '@/components/wrappers/Section';

import devices from '@/utilities/devices';
import { renderScript, renderUnderline } from '@/utilities/functions';

// #region STYLE
const ArticlesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  width: 90%;
  padding: 4em 0;
  margin: auto;
  @media screen and (${devices.mobile}) {
    width: 100%;
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    width: 100%;
  }
  @media screen and (${devices.minLaptop}) and (${devices.maxLaptop}) {
    width: 95%;
  }
`;

const Article = styled.a`
  text-decoration: none;
  color: ${props => props.theme.color.black};
  width: 20%;
  padding: 1em;
  margin: 1em;
  border: 2px solid rgba(0,0,0,.1);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: .2s;
  .media {
    font-weight: bold;
    font-size: 1.15em;
    padding: .2em 0 1em;
  }
  .title {
    width: 100%;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
  .date {
    display: inline-block;
    padding: 1em 0 .2em;
    font-size: .85em;
    letter-spacing: 1px;
  }
  svg {
    font-size: 1.4em;
    padding-left: 2em;
    opacity: .5;
    transition: .3s;
  }
  :hover {
    box-shadow: 0px -1px 15px 4px rgba(0,0,0, .05);
    transform: translateY(-2px);
    svg {
      opacity: 1;
    }
  }
  @media screen and (${devices.mobile}) {
    width: 90%;
    margin: 1em 2em;
    svg {
      padding-left: .5em;
    }
  }
  @media screen and (${devices.minTablet}) and (${devices.maxTablet}) {
    width: 40%;
  }
  @media screen and (${devices.minLaptop}) and (${devices.maxLaptop}) {
    width: 26%;
  }
`;
// #endregion

// #region COMPONENT
const PressArticles = ({ currentPath, currentLang }) => {

	const { t } = useTranslation('pressArticles');
	const scrollTo = useRef(null);

	// #region RENDER
	return (
		<PageWrapper
			title={t('meta.title')}
			description={t('meta.description')}
			introImageUrl="/src/public/img/press_allArticles.jpg"
			alternateUrl={{ fr: '', en: '', es: '' }}
			currentPath={currentPath}
			currentLang={currentLang}
		>
			<Head>
				{renderScript({
					'@context': 'https://schema.org',
					'@type': 'BreadcrumbList',
					'itemListElement': [{
						'@type': 'ListItem',
						'position': 1,
						'name': 'Accueil',
						'item': 'https://www.bruce.work/',
					}, {
						'@type': 'ListItem',
						'position': 2,
						'name': 'Presse',
						'item': 'https://www.bruce.work/presse/',
					}, {
						'@type': 'ListItem',
						'position': 3,
						'name': 'Tous les articles',
						'item': 'https://www.bruce.work/presse/tous-les-articles',
					}],
				})}
			</Head>
			<IntroSection underlineColor="blue" background="/img/press_allArticles.jpg" extraSpace scrollTo={scrollTo}>
				<h1>{renderUnderline(t('introSection.h1', { returnObjects: true }))}</h1>
			</IntroSection>
			<Section scrollTo={scrollTo} height="auto">
				<ArticlesContainer>
					{t('articles', { returnObjects: true }).map((article, key) => (
						<Article key={key} href={article.url} target="_blank" rel="noreferrer">
							<div>
								<div className="media"> {article.media} </div>
								<div className="title"> {article.title} </div>
								<div className="date"> {article.date} </div>
							</div>
							<div>
								<FiExternalLink />
							</div>
						</Article>
					))}
				</ArticlesContainer>
			</Section>
		</PageWrapper>
	);
	// #endregion
};
// #endregion

// #region CONNEXION
export const getServerSideProps = async ({ query }) => {
	return {
		props: {
			...await serverSideTranslations(query.language, ['pressArticles', 'navbar', 'footer'], nextI18NextConfig),
		},
	};
};
// #endregion

export default PressArticles;
